import { InfoIcon } from '@chakra-ui/icons';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Tooltip,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import BigNumber from 'bignumber.js';
import { Select } from 'chakra-react-select';
import { useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { noop, routes } from '@blockpulse3/data/shared';
import { ErrorMessage } from '@blockpulse3/ui/commons';

import { useGetCompanyQuery } from '@blockpulse3/graphql/hooks';
import {
  AllowedHoldingMethodSelect,
  defaultHoldingMethodOptions,
} from '../../../AllowedHoldingMethodSelect';
import { getPricePerShare, getValuation } from '../../../utils';
import { privateFundraisingParametersSchema } from '../NewPrivateFundraisingSubscribers/schema';
import { privateSubscriptionPeriodOptions } from '../schema';
import { IPrivateFundraisingParametersForm } from '../types';

type Props = {
  /* ** On submit loading ** */
  isLoading?: boolean;
  /* ** Optional loading text to render on the submit button ** */
  loadingText?: string;
  /* ** Default values of the form depending on the page (create & parameters) ** */
  defaultValues?: IPrivateFundraisingParametersForm;
  /* ** Callback on submit form ** */
  onSubmit?: (data: IPrivateFundraisingParametersForm) => void;
};

/**
 * NewPrivateFundraisingInformations.
 * Private fundraising parameters form used in both Create and Update pages.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function NewPrivateFundraisingInformations({
  isLoading = false,
  loadingText = '',
  defaultValues,
  onSubmit = noop,
}: Props): JSX.Element {
  const i18nFundraisingParametersForm = useTranslations('FundraisingParametersForm');

  const navigate = useNavigate();
  const { companyId = '' } = useParams();
  const companyReq = useGetCompanyQuery({
    variables: { companyId },
    skip: !companyId,
  });
  const company = companyReq?.data?.company;

  const { register, control, formState, handleSubmit, reset, setValue } =
    useForm<IPrivateFundraisingParametersForm>({
      defaultValues,
      resolver: yupResolver(privateFundraisingParametersSchema),
    });

  const { onChange: onValuationChange, ...registerValuation } = register('valuation', {
    valueAsNumber: true,
  });
  const { onChange: onPricePerShareChange, ...registerPricePerShare } = register('pricePerShare', {
    valueAsNumber: true,
  });

  const handleValuationChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (company?.actionsOwnedByCompany) {
      const valuation = new BigNumber(e.target.value);
      const pricePerShare = getPricePerShare(valuation, company.actionsOwnedByCompany);
      setValue('pricePerShare', pricePerShare);
    }
    onValuationChange(e);
  };

  const handlePricePerShareChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (company?.actionsOwnedByCompany) {
      const sharePrice = new BigNumber(e.target.value);
      const valuation = getValuation(sharePrice, company.actionsOwnedByCompany);
      setValue('valuation', valuation);
    }
    onPricePerShareChange(e);
  };

  const handleFormCancel = (): void => {
    if (company) {
      navigate(routes.workspace.fmt(companyId));
    }
  };

  const handleFormSubmit: SubmitHandler<IPrivateFundraisingParametersForm> = (data): void => {
    if (company) {
      onSubmit(data);
    }
  };

  useEffect(() => {
    reset({ ...defaultValues });
  }, [defaultValues, reset]);

  return (
    <Card variant="divider-top">
      <CardHeader>
        <Heading size="lg">{i18nFundraisingParametersForm('FormTitle')}</Heading>
      </CardHeader>
      <Divider />
      <CardBody>
        <form id="private-fundraising-parameters" onSubmit={handleSubmit(handleFormSubmit)}>
          <Stack spacing="4">
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <FormControl isInvalid={!!formState.errors?.name}>
                <FormLabel htmlFor="line">{i18nFundraisingParametersForm('Name')}</FormLabel>
                <Input id="line" type="string" {...register('name')} />
                <ErrorMessage error={formState.errors.name} />
              </FormControl>
              <Controller
                control={control}
                name="subscriptionPeriod"
                render={({ field }): JSX.Element => (
                  <FormControl isInvalid={!!formState.errors?.subscriptionPeriod}>
                    <HStack alignItems="flex-start" spacing="0">
                      <FormLabel htmlFor="city">
                        {i18nFundraisingParametersForm('SubscriptionPeriod')}
                        <Tooltip
                          hasArrow
                          label={i18nFundraisingParametersForm('Tooltip.SubscriptionPeriod')}
                          placement="top"
                        >
                          <Icon as={InfoIcon} color="gray.500" ml="2" />
                        </Tooltip>
                      </FormLabel>
                    </HStack>
                    <Select
                      isSearchable={false}
                      menuPlacement="auto"
                      options={privateSubscriptionPeriodOptions}
                      {...field}
                    />
                  </FormControl>
                )}
              />
            </Stack>
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <Stack spacing="4" w={{ base: '100%', md: '50%' }}>
                <FormControl isInvalid={!!formState.errors?.valuation}>
                  <FormLabel htmlFor="line">{i18nFundraisingParametersForm('Valuation')}</FormLabel>
                  <InputGroup>
                    <Input
                      id="valuation"
                      step="0.01"
                      type="number"
                      onChange={handleValuationChange}
                      {...registerValuation}
                    />
                    <InputRightElement color="gray.500">€</InputRightElement>
                  </InputGroup>
                  <ErrorMessage error={formState.errors.valuation} />
                </FormControl>
                <FormControl isInvalid={!!formState.errors?.pricePerShare}>
                  <FormLabel htmlFor="pricePerShare">
                    {i18nFundraisingParametersForm('PricePerActionField')}
                  </FormLabel>
                  <InputGroup>
                    <Input
                      id="pricePerShare"
                      step="0.01"
                      type="number"
                      onChange={handlePricePerShareChange}
                      {...registerPricePerShare}
                    />
                    <InputRightElement color="gray.500">€</InputRightElement>
                  </InputGroup>
                  <ErrorMessage error={formState.errors.pricePerShare} />
                </FormControl>
              </Stack>
              <Stack spacing="4" w={{ base: '100%', md: '50%' }}>
                <Controller
                  control={control}
                  name="assetType"
                  render={({ field }): JSX.Element => (
                    <FormControl isInvalid={!!formState.errors?.assetType}>
                      <FormLabel htmlFor="assetType">
                        {i18nFundraisingParametersForm('ActionType')}
                      </FormLabel>
                      <Select
                        isDisabled
                        id="assetType"
                        menuPlacement="auto"
                        value={{
                          label: i18nFundraisingParametersForm('OrdinaryShare'),
                          value: field.value,
                        }}
                      />
                      <ErrorMessage error={formState.errors?.assetType} />
                    </FormControl>
                  )}
                />
                <Controller
                  control={control}
                  name="allowedHoldingMethods"
                  render={({ field }): JSX.Element => (
                    <FormControl isInvalid={!!formState.errors?.allowedHoldingMethods}>
                      <HStack alignItems="flex-start" spacing="0">
                        <FormLabel htmlFor="allowedHoldingMethods">
                          {i18nFundraisingParametersForm('EligibleHoldingMethods')}
                        </FormLabel>
                      </HStack>
                      <AllowedHoldingMethodSelect
                        options={defaultHoldingMethodOptions}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                  )}
                />
              </Stack>
            </Stack>
          </Stack>
        </form>
      </CardBody>
      <CardFooter as={Stack} direction={{ base: 'column', md: 'row' }} spacing="4">
        <Button type="button" variant="secondary" w="full" onClick={handleFormCancel}>
          {i18nFundraisingParametersForm('Dashboard')}
        </Button>
        <Button
          data-cy="next"
          form="private-fundraising-parameters"
          isLoading={isLoading}
          loadingText={loadingText}
          type="submit"
          w="full"
        >
          {i18nFundraisingParametersForm('Next')}
        </Button>
      </CardFooter>
    </Card>
  );
}

export type NewPrivateFundraisingInformationsProps = Props;
